import { useState, useEffect, useRef } from "react";
import { useFetcher } from "react-router";
import { SubmitTarget } from "@tsTypes/application/fetcherSubmissionTarget";

export const usePost = (path: string, submissionTimeoutMs?: number) => {
  const fetcher = useFetcher();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimeoutIfExists = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const handleSubmit = async (data: SubmitTarget) => {
    setIsSubmitting(true);
    setError(null);

    await fetcher.submit(data, {
      method: "post",
      encType: "application/json",
      action: path,
    });

    clearTimeoutIfExists();

    if (submissionTimeoutMs) {
      timeoutRef.current = setTimeout(() => {
        setIsSubmitting(false);
        setError("Something went wrong. Please try again.");
      }, submissionTimeoutMs);
    }
  };

  useEffect(() => {
    if (fetcher.state === "idle") {
      clearTimeoutIfExists();
      setIsSubmitting(false);

      if (
        fetcher.data &&
        typeof fetcher.data === "object" &&
        "error" in fetcher.data &&
        typeof fetcher.data.error === "string"
      ) {
        setError(fetcher.data.error);
      }
    }
  }, [fetcher.state, fetcher.data]);

  useEffect(() => {
    return () => clearTimeoutIfExists();
  }, []);

  return {
    onSubmit: handleSubmit,
    data: fetcher.data,
    loading: isSubmitting,
    // Raw fetcher state, updated in the same commit as the router state.
    // Prefer this over `loading` for overlay-lock conditions: `loading` is
    // released via an effect one commit later, which breaks the lock
    // controller's same-microtask hand-off coalescing.
    state: fetcher.state,
    error,
    setError,
  };
};
